import { useState, useEffect, useCallback, useMemo, type UIEvent } from "react";
import { usePaginatedQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check, ArrowLeft, ChevronRight, Loader2 } from "lucide-react";

/** Shape of a CSI code record from Convex */
interface CSICodeRecord {
  _id: string;
  code: string;
  title: string;
  level: number;
  parentCode: string;
  divisionCode: string;
  divisionTitle: string;
}

interface CSICodePickerProps {
  value?: string;
  onSelect: (code: string, title: string, divisionCode?: string, divisionTitle?: string) => void;
  className?: string;
  disabled?: boolean;
}

const PAGE_SIZE = 20;

/** Returns true when the first non-whitespace character is a digit. */
function isCodeSearch(input: string): boolean {
  return /^\s*\d/.test(input);
}

/**
 * Normalize CSI code input for searching.
 * Converts "011100" → "01 11 00" to match DB storage format.
 * Preserves partial inputs like "01" → "01", "0111" → "01 11".
 */
function normalizeCSICodeInput(input: string): string {
  // Remove all spaces to get raw digits
  const digits = input.replace(/\s+/g, '');
  if (digits.length < 2) return digits;

  // Insert space every 2 characters
  return digits.match(/.{1,2}/g)?.join(' ') ?? digits;
}

function getTriggerLabel(value?: string): string {
  if (!value) return "Select CSI Code...";
  return value;
}

function getBrowseHeading(
  browseStack: string[],
  browseChildren: CSICodeRecord[],
  currentParentCode: string | null
): string {
  if (browseStack.length === 0) return "Divisions";
  if (browseChildren.length > 0) {
    const first = browseChildren[0];
    if (browseStack.length === 1) return first.divisionTitle;
    return `${currentParentCode} - ${first.divisionTitle}`;
  }
  return currentParentCode ?? "Browse";
}
export function CSICodePicker({
  value,
  onSelect,
  className,
  disabled,
}: CSICodePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Navigation stack for hierarchical browse: each entry is a parentCode to list children of.
  // Empty stack = show divisions (level 1). One entry = groups under a division. Two = sections.
  const [browseStack, setBrowseStack] = useState<string[]>([]);

  // Debounce the search input by 200ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 200);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Reset internal state when popover closes
  useEffect(() => {
    if (!open) {
      setInputValue("");
      setDebouncedSearch("");
      setBrowseStack([]);
    }
  }, [open]);

  const isSearching = debouncedSearch.length >= 2;
  const isCodeMode = isCodeSearch(debouncedSearch);

  // Normalize code input for searching: "011100" → "01 11 00"
  const normalizedCodePrefix = isCodeMode
    ? normalizeCSICodeInput(debouncedSearch.trim())
    : debouncedSearch;

  // --- Convex queries ---

  // Title-based text search (when input is alphabetic)
  const titleSearchQuery = usePaginatedQuery(
    api.csiCodes.search,
    open && isSearching && !isCodeMode ? { query: debouncedSearch } : "skip",
    { initialNumItems: PAGE_SIZE }
  );

  // Code-prefix search (when input starts with a digit)
  // Uses normalized prefix to match DB format (e.g., "011100" → "01 11 00")
  const codeSearchQuery = usePaginatedQuery(
    api.csiCodes.searchByCode,
    open && isSearching && isCodeMode
      ? { prefix: normalizedCodePrefix }
      : "skip",
    { initialNumItems: PAGE_SIZE }
  );

  const searchResults = isCodeMode ? codeSearchQuery.results : titleSearchQuery.results;
  const searchStatus = isCodeMode ? codeSearchQuery.status : titleSearchQuery.status;
  const searchLoadMore = isCodeMode ? codeSearchQuery.loadMore : titleSearchQuery.loadMore;

  // Browse: divisions (level 1)
  const divisionsQuery = usePaginatedQuery(
    api.csiCodes.listDivisions,
    open && !isSearching && browseStack.length === 0 ? {} : "skip",
    { initialNumItems: PAGE_SIZE }
  );
  const divisions = divisionsQuery.results;

  // Browse: children of the current browse level
  const currentParentCode = browseStack.length > 0 ? browseStack[browseStack.length - 1] : null;
  const browseChildrenQuery = usePaginatedQuery(
    api.csiCodes.listChildren,
    open && !isSearching && currentParentCode !== null
      ? { parentCode: currentParentCode }
      : "skip",
    { initialNumItems: PAGE_SIZE }
  );
  const browseChildren = browseChildrenQuery.results;

  const handleSelect = useCallback(
    (code: string, title: string, divisionCode?: string, divisionTitle?: string) => {
      onSelect(code, title, divisionCode, divisionTitle);
      setOpen(false);
    },
    [onSelect]
  );

  const handleDrillDown = useCallback((parentCode: string) => {
    setBrowseStack((prev) => [...prev, parentCode]);
  }, []);

  const handleBack = useCallback(() => {
    setBrowseStack((prev) => prev.slice(0, -1));
  }, []);

  const activePagination = useMemo(() => {
    if (isSearching) {
      return { status: searchStatus, loadMore: searchLoadMore };
    }
    if (browseStack.length === 0) {
      return {
        status: divisionsQuery.status,
        loadMore: divisionsQuery.loadMore,
      };
    }
    return {
      status: browseChildrenQuery.status,
      loadMore: browseChildrenQuery.loadMore,
    };
  }, [
    isSearching,
    searchStatus,
    searchLoadMore,
    browseStack.length,
    divisionsQuery.status,
    divisionsQuery.loadMore,
    browseChildrenQuery.status,
    browseChildrenQuery.loadMore,
  ]);

  const handleListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (activePagination.status !== "CanLoadMore") return;

      const target = event.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 48;
      if (nearBottom) {
        activePagination.loadMore(PAGE_SIZE);
      }
    },
    [activePagination]
  );

  // Group search results by division title for display
  const groupedSearchResults = useMemo(() => {
    if (!isSearching || searchResults.length === 0) return null;
    const groups: Record<string, CSICodeRecord[]> = {};
    for (const result of searchResults) {
      const key = result.divisionTitle;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    }
    return groups;
  }, [isSearching, searchResults]);

  const triggerLabel = getTriggerLabel(value);

  // Compute heading for the current browse level
  const browseHeading = useMemo(
    () => getBrowseHeading(browseStack, browseChildren, currentParentCode),
    [browseStack, browseChildren, currentParentCode]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          role="combobox"
          aria-expanded={open}
          aria-label="Select CSI MasterFormat code"
          disabled={disabled}
          className={cn(
            "h-6 justify-between border bg-background text-[10px] font-normal px-1.5",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or code (e.g. 08 11)..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList onScroll={handleListScroll}>
            <CommandEmpty>
              {isSearching
                ? searchStatus === "LoadingFirstPage"
                  ? "Searching..."
                  : "No CSI codes found."
                : "No results."}
            </CommandEmpty>

            {/* Search mode: show results grouped by division */}
            {isSearching && groupedSearchResults && (
              <>
                {Object.entries(groupedSearchResults).map(([divisionTitle, codes]) => (
                  <CommandGroup key={divisionTitle} heading={divisionTitle}>
                    {codes.map((code) => (
                      <CommandItem
                        key={code._id}
                        value={code.code}
                        onSelect={() => handleSelect(code.code, code.title, code.divisionCode, code.divisionTitle)}
                        className="flex items-center gap-2"
                      >
                        <Check
                          className={cn(
                            "size-4 shrink-0",
                            value === code.code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-xs font-medium shrink-0">{code.code}</span>
                        <span className="truncate text-muted-foreground">{code.title}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </>
            )}

            {/* Search loading state */}
            {isSearching && searchStatus === "LoadingFirstPage" && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Searching...
              </div>
            )}

            {/* Browse mode: division list (stack empty) */}
            {!isSearching && browseStack.length === 0 && (
              <CommandGroup heading="Divisions">
                {divisionsQuery.status === "LoadingFirstPage" && divisions.length === 0 && (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    Loading divisions...
                  </div>
                )}
                {divisions.map((division) => (
                  <CommandItem
                    key={division._id}
                    value={division.code}
                    onSelect={() => handleDrillDown(division.code)}
                    className="flex items-center gap-2"
                  >
                    <span className="text-muted-foreground text-xs font-medium shrink-0">
                      {division.code}
                    </span>
                    <span className="truncate">{division.title}</span>
                    <ChevronRight className="ml-auto size-4 shrink-0 opacity-50" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Browse mode: hierarchical children (stack non-empty) */}
            {!isSearching && browseStack.length > 0 && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={handleBack}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <ArrowLeft className="size-4 shrink-0" />
                    <span className="font-semibold">Back</span>
                  </CommandItem>
                </CommandGroup>

                <CommandGroup heading={browseHeading}>
                  {browseChildrenQuery.status === "LoadingFirstPage" &&
                    browseChildren.length === 0 && (
                    <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading...
                    </div>
                    )}
                  {browseChildren.map((code) => {
                    // Items with children get a drill-down chevron; leaf items are selectable
                    const hasChildren = code.code.endsWith(" 00") && code.level < 3;

                    if (hasChildren) {
                      return (
                        <CommandItem
                          key={code._id}
                          value={code.code}
                          onSelect={() => handleDrillDown(code.code)}
                          className="flex items-center gap-2"
                        >
                          <span className="text-muted-foreground text-xs font-medium shrink-0">
                            {code.code}
                          </span>
                          <span className="truncate">{code.title}</span>
                          <ChevronRight className="ml-auto size-4 shrink-0 opacity-50" />
                        </CommandItem>
                      );
                    }

                    return (
                      <CommandItem
                        key={code._id}
                        value={code.code}
                        onSelect={() => handleSelect(code.code, code.title, code.divisionCode, code.divisionTitle)}
                        className="flex items-center gap-2"
                      >
                        <Check
                          className={cn(
                            "size-4 shrink-0",
                            value === code.code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-xs font-medium shrink-0">{code.code}</span>
                        <span className="truncate text-muted-foreground">{code.title}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}

            {activePagination.status === "LoadingMore" && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Loading more...
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
